import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  Loader2,
  Printer,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  fetchBilanEvenement,
  fetchEvenementsBureau,
  type BilanEvenement,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { nomEdition, nomSansMillesime } from '../utils/evenement';
import { euros } from '../utils/format';
import { imprimerListe } from '../utils/impression';

/**
 * Prévisionnel d'achats d'une festivité, à partir de son édition précédente.
 *
 * Préparer la fête votive 2027, c'est relire ce qu'on a acheté pour celle de
 * 2026 : combien de fûts, combien d'écocups, pour quel montant. La page met
 * les deux éditions côte à côte, ligne à ligne par libellé — d'où
 * l'importance de ressaisir le même article sous le même nom — et laisse
 * voir ce qui manque encore à l'édition qui se prépare.
 */
export default function PrevisionnelAchatsPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const aAcces = aLeDroit('suivre_achats');
  const perso = usePersonnalisation();

  const [cibleId, setCibleId] = useState<number | ''>('');
  const [referenceId, setReferenceId] = useState<number | ''>('');

  const evenementsQuery = useQuery({
    queryKey: ['bureau-evenements'],
    queryFn: fetchEvenementsBureau,
    enabled: aAcces,
  });

  const bilan = useQuery({
    queryKey: ['bilan-evenement', cibleId, referenceId],
    queryFn: () => fetchBilanEvenement(Number(cibleId), referenceId === '' ? undefined : Number(referenceId)),
    enabled: aAcces && cibleId !== '',
  });

  // Les deux listes d'achats rapprochées par libellé : une ligne par article,
  // l'édition de référence à gauche, celle qui se prépare à droite.
  const lignes = useMemo(() => {
    const cible = bilan.data?.cible;
    const reference = bilan.data?.reference;
    const parLibelle = new Map<
      string,
      { libelle: string; ref?: { quantite: number; montant: number }; cible?: { quantite: number; montant: number } }
    >();

    for (const l of reference?.achats.lignes ?? []) {
      const entree = parLibelle.get(l.libelle) ?? { libelle: l.libelle };
      entree.ref = {
        quantite: (entree.ref?.quantite ?? 0) + l.quantite,
        montant: (entree.ref?.montant ?? 0) + l.montant,
      };
      parLibelle.set(l.libelle, entree);
    }
    for (const l of cible?.achats.lignes ?? []) {
      const entree = parLibelle.get(l.libelle) ?? { libelle: l.libelle };
      entree.cible = {
        quantite: (entree.cible?.quantite ?? 0) + l.quantite,
        montant: (entree.cible?.montant ?? 0) + l.montant,
      };
      parLibelle.set(l.libelle, entree);
    }

    return [...parLibelle.values()].sort(
      (a, b) => (b.ref?.montant ?? b.cible?.montant ?? 0) - (a.ref?.montant ?? a.cible?.montant ?? 0)
    );
  }, [bilan.data]);

  // Quand la festivité à préparer n'a pas encore de recettes — le cas
  // normal : elle n'a pas eu lieu — la croissance se mesure sur l'histoire
  // de la référence : l'édition de référence contre sa propre précédente.
  const referenceSansRecettesCible =
    bilan.data?.reference != null && (bilan.data?.cible?.ventes.total ?? 0) <= 0;
  const bilanHistorique = useQuery({
    queryKey: ['bilan-evenement-historique', bilan.data?.reference?.id],
    queryFn: () => fetchBilanEvenement(bilan.data!.reference!.id),
    enabled: aAcces && referenceSansRecettesCible,
  });

  // La croissance constatée, sur les recettes : entre les deux éditions
  // comparées si les deux ont vendu, sinon entre la référence et sa propre
  // édition précédente. Le résultat dit toujours d'où il vient.
  const croissance = useMemo(() => {
    const cible = bilan.data?.cible;
    const reference = bilan.data?.reference;

    if (cible && reference && reference.ventes.total > 0 && cible.ventes.total > 0) {
      return {
        taux: (cible.ventes.total - reference.ventes.total) / reference.ventes.total,
        ancienne: reference,
        recente: cible,
      };
    }

    const precedente = bilanHistorique.data?.reference;
    if (reference && precedente && precedente.ventes.total > 0 && reference.ventes.total > 0) {
      return {
        taux: (reference.ventes.total - precedente.ventes.total) / precedente.ventes.total,
        ancienne: precedente,
        recente: reference,
      };
    }

    return null;
  }, [bilan.data, bilanHistorique.data]);

  // Le plan de commande projeté : chaque article de la comparaison, ses
  // quantités et montants majorés de la croissance constatée. Une projection
  // arithmétique, rien de plus — le document le dit en toutes lettres.
  const plan = useMemo(() => {
    if (croissance === null || croissance.taux <= 0) {
      return null;
    }
    const facteur = 1 + croissance.taux;
    const articles = lignes.map((l) => {
      const base = l.cible ?? l.ref!;

      return {
        libelle: l.libelle,
        quantite: Math.ceil(base.quantite * facteur),
        montant: Math.round(base.montant * facteur * 100) / 100,
      };
    });

    return {
      articles,
      total: articles.reduce((somme, a) => somme + a.montant, 0),
    };
  }, [croissance, lignes]);

  const exporterPlan = () => {
    const cible = bilan.data?.cible;
    if (!plan || !cible || croissance === null) return;

    // La fenêtre d'impression du navigateur enregistre en PDF : le document
    // part en pièce jointe au fournisseur ou se range dans le classeur.
    imprimerListe({
      titre: `Plan de commande prévisionnel — ${nomSansMillesime(cible.nom)}`,
      association: perso?.nomAssociation ?? undefined,
      sousTitre:
        `Projection à +${(croissance.taux * 100).toFixed(1)} % à partir de la croissance constatée entre ` +
        `${nomEdition(croissance.ancienne.nom, croissance.ancienne.dateDebut)} et ${nomEdition(croissance.recente.nom, croissance.recente.dateDebut)}. ` +
        `Document indicatif : une projection arithmétique n'est pas une prévision — ajustez selon le terrain.`,
      totaux: [
        { libelle: 'de budget projeté', valeur: euros.format(plan.total) },
        { libelle: 'de croissance constatée', valeur: `+${(croissance.taux * 100).toFixed(1)} %` },
      ],
      colonnes: [
        { titre: 'Article', valeur: (a: { libelle: string }) => a.libelle },
        { titre: 'Quantité projetée', valeur: (a: { quantite: number }) => String(a.quantite), alignement: 'centre' },
        { titre: 'Budget projeté', valeur: (a: { montant: number }) => euros.format(a.montant), alignement: 'droite' },
        { titre: 'Fournisseur retenu', valeur: () => '', alignement: 'gauche' },
      ],
      lignes: plan.articles,
      colonnePointage: 'Commandé',
    });
  };

  if (authLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!aAcces) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Le prévisionnel dépend de votre fonction au bureau : un administrateur peut
              l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const evenements: { id: number; nom: string; dateDebut?: string }[] =
    evenementsQuery.data?.evenements ?? [];
  const cible = bilan.data?.cible ?? null;
  const reference = bilan.data?.reference ?? null;

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <Link
        to="/bureau/achats"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400"
      >
        <ArrowLeft className="w-4 h-4" /> Suivi des achats
      </Link>

      <div className="flex items-center gap-3">
        <TrendingDown className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Prévisionnel d'achats
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Préparer une festivité en relisant ce que la précédente a coûté, article par
            article.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Festivité à préparer
          <select
            className="input mt-1"
            value={cibleId}
            onChange={(e) => {
              setCibleId(e.target.value === '' ? '' : Number(e.target.value));
              setReferenceId('');
            }}
          >
            <option value="">Choisir…</option>
            {evenements.map((ev) => (
              <option key={ev.id} value={ev.id}>{nomEdition(ev.nom, (ev as any).dateDebut)}</option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Édition de référence
          <select
            className="input mt-1"
            value={referenceId}
            onChange={(e) => setReferenceId(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={cibleId === ''}
          >
            {/* Vide : le serveur devine l'édition précédente par le nom. */}
            <option value="">Automatique — l'édition précédente du même nom</option>
            {evenements
              .filter((ev) => ev.id !== cibleId)
              .map((ev) => (
                <option key={ev.id} value={ev.id}>{nomEdition(ev.nom, (ev as any).dateDebut)}</option>
              ))}
          </select>
        </label>
      </div>

      {cibleId === '' ? (
        <div className="card lg:p-8 text-center space-y-3">
          <CalendarDays className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-600 dark:text-gray-400">
            Choisissez la festivité à préparer : ses achats se comparent à ceux de
            l'édition précédente.
          </p>
        </div>
      ) : bilan.isPending ? (
        <div className="card p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CarteEdition titre="Référence" bilan={reference} vide="Aucune édition précédente trouvée — choisissez-la à la main ci-dessus." />
            <CarteEdition titre="En préparation" bilan={cible} accent />
          </div>

          <section className="card p-0 overflow-x-auto">
            {lignes.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Aucun achat rattaché à ces festivités. Rattachez les dépenses à leur
                événement depuis le suivi des achats.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 font-medium">Article</th>
                    <th className="px-4 py-3 font-medium text-center" colSpan={2}>
                      {reference ? nomEdition(reference.nom, reference.dateDebut) : 'Référence'}
                    </th>
                    <th className="px-4 py-3 font-medium text-center" colSpan={2}>
                      {cible ? nomEdition(cible.nom, cible.dateDebut) : 'En préparation'}
                    </th>
                  </tr>
                  <tr className="text-xs">
                    <th className="px-4 pb-2"></th>
                    <th className="px-4 pb-2 font-medium text-center">Qté</th>
                    <th className="px-4 pb-2 font-medium text-right">Montant</th>
                    <th className="px-4 pb-2 font-medium text-center">Qté</th>
                    <th className="px-4 pb-2 font-medium text-right">Montant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {lignes.map((l) => (
                    <tr key={l.libelle}>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {l.libelle}
                        {/* L'article de référence pas encore repris : c'est la
                            ligne qu'on vient chercher pour ne rien oublier. */}
                        {l.ref && !l.cible && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            à prévoir
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums text-gray-600 dark:text-gray-400">
                        {l.ref?.quantite ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600 dark:text-gray-400">
                        {l.ref ? euros.format(l.ref.montant) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums">
                        {l.cible?.quantite ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {l.cible ? euros.format(l.cible.montant) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-800 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td></td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {reference ? euros.format(reference.achats.total) : '—'}
                    </td>
                    <td></td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {cible ? euros.format(cible.achats.total) : '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </section>

          {/* La croissance constatée, et ce qu'elle suggère pour la suite. */}
          {croissance !== null && (
            <section
              className={`card ${
                plan
                  ? 'border-2 border-green-200 dark:border-green-800'
                  : ''
              }`}
            >
              <div className="flex items-center gap-3">
                {croissance.taux >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-green-600 shrink-0" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600 shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    Croissance constatée : {croissance.taux >= 0 ? '+' : ''}
                    {(croissance.taux * 100).toFixed(1)} % de recettes, de{' '}
                    {nomEdition(croissance.ancienne.nom, croissance.ancienne.dateDebut)} à{' '}
                    {nomEdition(croissance.recente.nom, croissance.recente.dateDebut)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {plan
                      ? 'La fête grandit : voici ce que donnerait la prochaine commande si la tendance se poursuivait.'
                      : 'Recettes en repli : pas de majoration à projeter, repartez des quantités de la dernière édition.'}
                  </p>
                </div>
              </div>

              {plan && (
                <>
                  <div className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                    <strong>Projection, pas prophétie</strong> — ces quantités appliquent simplement la
                    croissance constatée aux achats de la dernière édition. Personne n'est voyant :
                    ajustez selon la météo, la programmation et votre connaissance du terrain.
                  </div>

                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-gray-600 dark:text-gray-300">
                        <tr>
                          <th className="px-4 py-2 font-medium">Article</th>
                          <th className="px-4 py-2 font-medium text-center">Quantité projetée</th>
                          <th className="px-4 py-2 font-medium text-right">Budget projeté</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {plan.articles.map((a) => (
                          <tr key={a.libelle}>
                            <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                              {a.libelle}
                            </td>
                            <td className="px-4 py-2.5 text-center tabular-nums">{a.quantite}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">
                              {euros.format(a.montant)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="font-semibold">
                        <tr className="border-t-2 border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-2.5">Budget total projeté</td>
                          <td></td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {euros.format(plan.total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <button
                    type="button"
                    onClick={exporterPlan}
                    className="mt-4 btn-outline inline-flex items-center gap-2 text-sm px-4 py-2"
                  >
                    <Printer className="w-4 h-4" /> Exporter le plan en PDF
                  </button>
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Le résumé d'une édition : ce qu'elle a coûté, rapporté, et son solde.
 */
function CarteEdition({
  titre,
  bilan,
  accent = false,
  vide,
}: {
  titre: string;
  bilan: BilanEvenement | null;
  accent?: boolean;
  vide?: string;
}) {
  if (!bilan) {
    return (
      <div className="card py-4 text-center text-sm text-gray-500 dark:text-gray-400">
        {vide ?? '—'}
      </div>
    );
  }

  return (
    <div className={`card py-4 px-5 ${accent ? 'border-2 border-primary-200 dark:border-primary-800' : ''}`}>
      <p className="text-xs text-gray-500 dark:text-gray-400">{titre}</p>
      <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{nomEdition(bilan.nom, bilan.dateDebut)}</p>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center text-sm">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Recettes</p>
          <p className="font-bold tabular-nums text-green-700 dark:text-green-400">
            {euros.format(bilan.ventes.total)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Achats</p>
          <p className="font-bold tabular-nums text-red-700 dark:text-red-400">
            {euros.format(bilan.achats.total)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Solde</p>
          <p
            className={`font-bold tabular-nums ${
              bilan.solde >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
            }`}
          >
            {euros.format(bilan.solde)}
          </p>
        </div>
      </div>
    </div>
  );
}
